<template>
  <div class="bg-white w-full rounded-lg divide-y divide-slate-100 relative overflow-hidden shadow flex flex-col">
    <header :class="setHeaderBg" class="flex items-center space-x-2 p-4">
      <img :src="setIcon" class="flex-shrink-0" />
      <div class="w-full leading-tight">
        <h4 class="text-slate-700">{{ data.name }}</h4>
        <p :class="setColorText" class="font-bold">{{ data | status }}</p>
      </div>
    </header>
    <div class="px-4 py-4">
      <span class="text-xs uppercase text-slate-600 block mb-1">Socios</span>
      <div class="flex items-center justify-between" @click="showUserList">
        <ul class="flex items-center avatar-list cursor-pointer w-full">
          <li v-for="member in shortMemberList" :key="member.id">
            <base-list-avatar :src="member | image" :text="member | letter" />
          </li>
          <li v-if="totalMembers > 0">
            <base-list-avatar :text="'+' + totalMembers" />
          </li>
        </ul>
        <span class="text-sm cursor-pointer block px-2 py-1 bg-secondary-50 text-secondary rounded hover:text-white hover:bg-secondary flex-shrink-0">Ver socios</span>
      </div>
    </div>
    <div class="p-4" v-if="isConfigurable || isOutstanding">
      <base-button v-if="isConfigurable" size="sm" text="Gestionar" @click="open" mode="secondary-phantom" custom="w-full" />
      <base-button v-else size="sm" text="Pagar" @click="paid" mode="secondary-phantom" custom="w-full" />
    </div>
    <div class="text-red-700 text-sm" v-if="data.status === 'blocked'">
      <div class="text-center p-4 leading-tight bg-red-50">
        <p class="font-bold">Incumplimiento de normas</p>
        <span class="opacity-80">Contactar con el RGCC</span>
      </div>
    </div>
    <div class="text-warning-dark text-sm" v-if="data.status === 'pending'">
      <div class="text-center p-4 leading-tight bg-warning-light">
        <p class="font-bold">Pendiente de validación</p>
        <span class="opacity-80">Contactar con el RGCC</span>
      </div>
    </div>
    <div :class="[userList ? 'translate-y-0' : 'translate-y-full']" class="absolute overflow-y-auto shadow z-20 bg-white w-full h-full top-0 left-0 transition-transform duration-300 transform">
      <header class="p-4 shadow flex items-center justify-between sticky top-0 border-b border-solid border-slate-100 bg-slate-50">
        <h4 class="text-lg font-bold uppercase font-sans">Socios</h4>
        <span @click="showUserList" class="text-sm cursor-pointer block px-2 py-1 bg-secondary-50 text-secondary rounded hover:text-white hover:bg-secondary">Cerrar</span>
      </header>
      <div class="">
        <ul class="divide-y divide-slate-100">
          <li v-for="member in data.members" :key="member.id" class="flex items-center space-x-2 px-4 py-2">
            <base-list-avatar :src="member | image" :text="member | letter" />
            <span>{{ member.name }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Pass from '@/utils/Pass'
import PassServices from '@/services/passes'
import TpvServices from '@/services/tpv'

export default {
  name: 'PassCard',
  props: {
    data: {
      type: Object,
      require: true
    },
    type: {
      type: String,
      require: true
    }
  },
  data: function () {
    return {
      userList: false
    }
  },
  computed: {
    membership: function () {
      return this.$store.getters['auth/membership']
    },
    isConfigurable: function () {
      return this.data.status === 'active' || this.data.status === 'inactive'
    },
    isOutstanding: function () {
      return this.data.status === 'outstanding'
    },
    totalMembers: function () {
      return this.data.members.length - 2
    },
    shortMemberList: function () {
      return this.data.members.slice(0, 2)
    },
    setHeaderBg: function () {
      return {
        'bg-red-50': this.data.status === 'blocked',
        'bg-green-50': this.data.status === 'active',
        'bg-gray-50': this.data.status === 'inactive',
        'bg-warning-light': this.data.status === 'pending' || this.data.status === 'pending-rgcc' || this.data.status === 'outstanding'
      }
    },
    setBorder: function () {
      return {
        'border-red-700': this.data.status === 'blocked',
        'border-green-800/50': this.data.status === 'active',
        'border-gray-700': this.data.status === 'inactive',
        'border-orange-700': this.data.status === 'pending' || this.data.status === 'pending-rgcc' || this.data.status === 'outstanding'
      }
    },
    setColorText: function () {
      return {
        'text-red-700': this.data.status === 'blocked',
        'text-green-700': this.data.status === 'active',
        'text-gray-500': this.data.status === 'inactive',
        'text-warning-dark': this.data.status === 'pending' || this.data.status === 'pending-rgcc' || this.data.status === 'outstanding'
      }
    },
    setIcon: function () {
      const images = require.context('@/assets/img/')
      let icon = null
      switch (this.data.status) {
        case 'blocked':
          icon = 'Status_Blocked.svg'
          break
        case 'active':
          icon = 'Status_Enabled.svg'
          break
        case 'pending':
        case 'pending-rgcc':
        case 'outstanding':
          icon = 'Status_Pending.svg'
          break
        default:
          icon = 'Status_Disabled.svg'
          break
      }
      return images('./' + icon)
    }
  },
  methods: {
    showUserList: function () {
      this.userList = !this.userList
    },
    open: function () {
      this.$emit('open')
    },
    paid: async function () {
      try {
        this.$store.commit('app/setLoading', true)
        const price = await this.getPrice()
        if (price) {
          await this.purchase(this.membership, this.type, this.data.id, price)
        } else {
          this.doFeedback('Se ha producido un problema para obtener el precio del producto')
        }
      } catch (error) {
        this.$store.commit('app/setLoading', false)
        this.doFeedback()
      }
    },
    getPrice: async function () {
      const response = await PassServices.getPrices(this.type)
      return response ? response[0].PrecioVenta : null
    },
    purchase: async function (membership, code, id, price) {
      let okUrl = process.env.VUE_APP_COURSE_PASS_PURCHASE_REDIRECT_URI_OK
      let koUrl = process.env.VUE_APP_COURSE_PASS_PURCHASE_REDIRECT_URI_KO

      if (this.type === 'P-DEP') {
        okUrl = process.env.VUE_APP_SPORT_PASS_PURCHASE_REDIRECT_URI_OK
        koUrl = process.env.VUE_APP_SPORT_PASS_PURCHASE_REDIRECT_URI_KO
      } else if (this.type === 'P-AUX') {
        okUrl = process.env.VUE_APP_AUXILIAR_PASS_PURCHASE_REDIRECT_URI_OK
        koUrl = process.env.VUE_APP_AUXILIAR_PASS_PURCHASE_REDIRECT_URI_KO
      }

      const response = await TpvServices.setup(membership, code, id, price, okUrl, koUrl)
      const data = {
        MerchantID: response.merchantID,
        AcquirerBIN: response.acquirerBIN,
        TerminalID: response.terminalID,
        Num_operacion: response.operationNumber,
        Importe: response.price,
        TipoMoneda: response.currency,
        Exponente: response.exponent,
        URL_OK: response.urlOk,
        URL_NOK: response.urlNok,
        Firma: response.signature,
        Cifrado: response.cypher,
        Pago_soportado: response.supportedPayment
      }

      const form = document.createElement('form')
      form.method = 'post'
      form.action = response.action
      for (const key in data) {
        if (Object.hasOwnProperty.call(data, key)) {
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = key
          hiddenField.value = data[key]

          form.appendChild(hiddenField)
        }
      }

      document.body.appendChild(form)
      form.submit()
    }
  },
  filters: {
    status: function (value) {
      return Pass.getStatusName(value.status)
    },
    image: function (value) {
      return value?.image ? value.image : null
    },
    letter: function (value) {
      return value?.name ? value.name.charAt(0) : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.avatar-list {
  li:first-child {
    margin-left: -4px;
  }
  li {
    margin-right: -25px;
  }
}
</style>
